/**
 * Goal Bingo hooks for React Query
 *
 * Provides data fetching and mutation hooks for goal bingo management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BingoCardFilters,
  CreateBingoCardInput,
  UpdateBingoCardInput,
  CreateBingoCellInput,
  UpdateBingoCellInput,
} from '@/types/bingo';
import * as bingoApi from '@/api/bingo';

// Query keys
export const bingoKeys = {
  all: ['bingo'] as const,
  cards: () => [...bingoKeys.all, 'cards'] as const,
  cardList: (filters?: BingoCardFilters) => [...bingoKeys.cards(), 'list', filters] as const,
  card: (id: string) => [...bingoKeys.cards(), 'detail', id] as const,
  cells: (cardId: string, status?: string) => [...bingoKeys.card(cardId), 'cells', status] as const,
  cell: (cardId: string, cellId: string) => [...bingoKeys.card(cardId), 'cell', cellId] as const,
  patterns: (cardId: string) => [...bingoKeys.card(cardId), 'patterns'] as const,
  progress: (cardId: string) => [...bingoKeys.card(cardId), 'progress'] as const,
};

/**
 * Hook for listing bingo cards
 */
export function useBingoCards(filters?: BingoCardFilters) {
  return useQuery({
    queryKey: bingoKeys.cardList(filters),
    queryFn: () => bingoApi.listBingoCards(filters),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for getting a single bingo card
 */
export function useBingoCard(cardId: string) {
  return useQuery({
    queryKey: bingoKeys.card(cardId),
    queryFn: () => bingoApi.getBingoCard(cardId),
    enabled: !!cardId,
    staleTime: 0,
  });
}

/**
 * Hook for listing cells in a card
 */
export function useBingoCells(cardId: string, status?: string) {
  return useQuery({
    queryKey: bingoKeys.cells(cardId, status),
    queryFn: () => bingoApi.listBingoCells(cardId, status),
    enabled: !!cardId,
    staleTime: 0,
  });
}

/**
 * Hook for getting a single cell
 */
export function useBingoCell(cardId: string, cellId: string) {
  return useQuery({
    queryKey: bingoKeys.cell(cardId, cellId),
    queryFn: () => bingoApi.getBingoCell(cardId, cellId),
    enabled: !!cardId && !!cellId,
    staleTime: 0,
  });
}

/**
 * Hook for getting patterns
 */
export function useBingoPatterns(cardId: string) {
  return useQuery({
    queryKey: bingoKeys.patterns(cardId),
    queryFn: () => bingoApi.listBingoPatterns(cardId),
    enabled: !!cardId,
    staleTime: 0,
  });
}

/**
 * Hook for getting progress
 */
export function useBingoProgress(cardId: string) {
  return useQuery({
    queryKey: bingoKeys.progress(cardId),
    queryFn: () => bingoApi.getBingoProgress(cardId),
    enabled: !!cardId,
    staleTime: 0,
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook for creating a bingo card
 */
export function useCreateBingoCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateBingoCardInput) => bingoApi.createBingoCard(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cards() });
    },
  });
}

/**
 * Hook for updating a bingo card
 */
export function useUpdateBingoCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, input }: { cardId: string; input: UpdateBingoCardInput }) =>
      bingoApi.updateBingoCard(cardId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.cards() });
    },
  });
}

/**
 * Hook for deleting a bingo card
 */
export function useDeleteBingoCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => bingoApi.deleteBingoCard(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cards() });
    },
  });
}

/**
 * Hook for creating a cell
 */
export function useCreateBingoCell() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, input }: { cardId: string; input: CreateBingoCellInput }) =>
      bingoApi.createBingoCell(cardId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cells(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.progress(variables.cardId) });
    },
  });
}

/**
 * Hook for updating a cell
 */
export function useUpdateBingoCell() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      cellId,
      input,
    }: {
      cardId: string;
      cellId: string;
      input: UpdateBingoCellInput;
    }) => bingoApi.updateBingoCell(cardId, cellId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cells(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.cell(variables.cardId, variables.cellId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.progress(variables.cardId) });
    },
  });
}

/**
 * Hook for deleting a cell
 */
export function useDeleteBingoCell() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, cellId }: { cardId: string; cellId: string }) =>
      bingoApi.deleteBingoCell(cardId, cellId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cells(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.progress(variables.cardId) });
    },
  });
}

/**
 * Hook for completing a cell
 */
export function useCompleteBingoCell() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, cellId }: { cardId: string; cellId: string }) =>
      bingoApi.completeBingoCell(cardId, cellId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bingoKeys.cells(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.cell(variables.cardId, variables.cellId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.card(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.patterns(variables.cardId) });
      queryClient.invalidateQueries({ queryKey: bingoKeys.progress(variables.cardId) });
    },
  });
}


